import { NgClass, TitleCasePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, effect, inject, input, OnInit, output, viewChild, ViewEncapsulation } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { ProjectObserverService } from '@app/cem/project/services/project-observer.service';
import { PlexEnvironment } from '@app/env';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { TabStripScrollingDirective } from '@app/shared/directives/kendohelpers/tabstrip-scrolling.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { SelectEvent, TabStripModule } from '@progress/kendo-angular-layout';
import { ErrorService } from 'app/common/services/error.service';
import { FunctionIds, ProjectStatusDescription, ProjectTypeId } from 'app/shared/data/project-enums';
import { ProjectTab } from 'app/shared/models/project/Project-Tab';
import { ProjectTabName } from '../../enums/project-tab-name';
import { ProjectEstimateHomeComponent } from '../../estimate/project-estimate-home/project-estimate-home.component';
import { AfeDataSharingService } from '../../services/afe-data-sharing.service';
import { ProjectScreenBehaviorService } from '../../services/project-screenbehavior.service';
import { SelectedTabService } from '../../services/selected-tab.service';
import { ProjectState } from '../../services/state/project-state';
import { ProjectAssetMappingComponent } from '../project-asset-mapping/project-asset-mapping.component';
import { TypeSubType } from '../project-create-new/project-create.models';
import { ProjectDetailsComponent } from '../project-details/project-details.component';
import { ProjectHeaderDataComponent } from '../project-header-data/project-header-data.component';
import { ProjectResourcesComponent } from '../project-resources/project-resources.component';
import { ProjectScheduleComponent } from '../project-schedule/project-schedule.component';
import { ProjectScopingIndexComponent } from '../project-scoping/project-scoping-index/project-scoping-index.component';
import { ProjectCreateNewComponent } from './../project-create-new/project-create-new.component';
import { ProjectPlanningComponent } from './../project-planning/project-planning.component';

@Component({
  selector: 'app-project-base-scenario',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './project-base-scenario.component.html',
  styleUrls: ['./project-base-scenario.component.scss'],
  imports: [
    ButtonDirective,
    ButtonLoadingDirective,
    MatToolbar,
    NgClass,
    ProjectAssetMappingComponent,
    ProjectCreateNewComponent,
    ProjectDetailsComponent,
    ProjectEstimateHomeComponent,
    ProjectHeaderDataComponent,
    ProjectPlanningComponent,
    ProjectScheduleComponent,
    ProjectScopingIndexComponent,
    TabStripModule,
    TabStripScrollingDirective,
    TitleCasePipe,
  ],
  providers: [AfeDataSharingService, ProjectObserverService, ProjectState]
})
export class ProjectBaseScenarioComponent implements OnInit, AfterViewInit {
  public selectedTabService = inject(SelectedTabService);
  private errorService = inject(ErrorService);
  private screenBehaviors = inject(ProjectScreenBehaviorService);
  private projectState = inject(ProjectState);
  private poSvc = inject(ProjectObserverService);
  public environment = inject(PlexEnvironment);

  public icons = kendoIcons;

  readonly _projectCreateComponent = viewChild(ProjectCreateNewComponent);
  readonly _projectHeaderDataComponent = viewChild(ProjectHeaderDataComponent);
  readonly _projectAssetMappingComponent = viewChild(ProjectAssetMappingComponent);
  readonly _projectResourcesComponent = viewChild(ProjectResourcesComponent);
  readonly _projectEstimateHomeComponent = viewChild(ProjectEstimateHomeComponent);
  readonly _projectDetailsComponent = viewChild(ProjectDetailsComponent);
  readonly _projectPlanningComponent = viewChild(ProjectPlanningComponent);
  readonly _projectScopingIndexComponent = viewChild(ProjectScopingIndexComponent);

  readonly projectData = input<ProjectTab>(undefined);
  readonly tabProjectId = input<number>(undefined);

  readonly closeNewProjectTab = output<any>();
  public projectProperties: ProjectTab;
  public isProjectValid: boolean = false;
  public formState: boolean = false;
  public formValid: boolean = false;
  public projectExists: boolean = false;
  public projectSubTypeId: number;
  public showNewProject: boolean = true;
  public showParameterTab: boolean;
  public showTankBatteryTabs: boolean;
  public showSWDFacilityPlanningTab: boolean;
  public headerTabTitle: string = 'HEADER';
  public teamTabTitle: string = 'TEAM';
  public projectAssetMappingTabTitle: string = 'ASSET Allocation';
  public assetMappingTabTitle: string = 'ASSET Allocation';
  public accountingTabTitle: string = 'ACCOUNTING';
  public scheduleTabTitle: string = 'SCHEDULE';
  public estimatesTabTitle: string = 'ESTIMATES';
  public parametersTabTitle: string = 'PARAMETERS';
  public resourcesTabTitle: string = 'RESOURCES';
  public eventLogTabTitle: string = 'EVENT LOG';
  public planningabTitle: string = 'PLANNING';
  public scopingTabTitle: string = 'SCOPING';
  public wellMappingTabTitle: string = 'WELL MAPPING';
  public headerTabTdv: boolean = false;
  public assetMappingTabTdv: boolean = false;
  public accountingTabTdv: boolean = false;
  public projectAssetMappingTabTdv: boolean = false;
  public scheduleTabTdv: boolean = false;
  public estimatesTabTdv: boolean = false;
  public parametersTabTdv: boolean = false;
  public parametersTabTdvCompletion: boolean = false;
  public resourcesTabTdv: boolean = false;
  public isAddingProject: boolean = false;
  public eventLogTabTdv: boolean = false;
  public planningTabTdv: boolean = false;
  public scopingTabTdv: boolean = false;
  public requireAssetAllocation: boolean;
  public showWellMappingTabFeatures: boolean;
  public saving: boolean;
  /**
   * Temporary solution to stop non-active tabs from loading
   * Author: Philip Choi
   * @type {boolean[]}
   */
  public tabContents: boolean[] = [
    true,
    false,
    false,
    false,
    false,
    false,
    false,
    false
  ];

  public validateTab: string[] = [];

  public validateTabs: any = {
    header: true,
    team: true,
    accounting: true,
    assetmapping: true
  };

  public allTabsValid: any = {
    header: true,
    team: true,
    accounting: true,
    assetmapping: true
  };

  constructor() {
    effect(async () => {
      let project = this.projectData();
      let statusDescription: string;
      if (project) {
        if (project.ProjectId === -1) {
          statusDescription = ProjectStatusDescription.Draft;
        } else {
          statusDescription = project.Project.ProjectStatus.Description;
        }
        this.projectState.set('project', project);
        this.projectState.set('status', statusDescription);
        this.projectProperties = project;
        this.projectSubTypeId = project.ProjectSubTypeId;
        this.showNewProject = project.ProjectId < 1;
        this.showParameterTab = this.getParameterVisibility(project.ProjectTypeId, project.ProjectSubTypeId);
        this.showTankBatteryTabs = project.ProjectTypeId === ProjectTypeId.TankBattery && project.FunctionId === FunctionIds.Facilities;
        this.showSWDFacilityPlanningTab = project.ProjectTypeId === ProjectTypeId.SaltWaterDisposal && project.FunctionId === FunctionIds.Facilities;
        this.selectedTabService = project.SelectedTabService;
      }

      const behaviors = await this.screenBehaviors.getByProjectTypeId(this.projectState.data.project.ProjectTypeId);
      var behavior = this.screenBehaviors.getBehaviorRule(behaviors, 'app-project-asset-mapping', 'AssetMapping');
      this.requireAssetAllocation = !behavior || behavior.Visible; //if no behavior assume true
    });

    effect(() => {
      let projectId = this.tabProjectId();
      // Project exists and is being opened by double clicking a Project from the list
      if (projectId > 1) {
        this.tabInputProjectId(projectId);
      }
    });
  }

  async ngOnInit() {
    this.poSvc.projectTypeWithParameters = this.showParameterTab;

    this.showWellMappingTabFeatures = this.environment.showWellMappingTabFeatures;
  }

  public ngAfterViewInit() {
    this.poSvc.projectTabs$.subscribe((f) => {
      this.projectState.set('validate', f);
      this.projectState.set('showParameterTab', this.showParameterTab);
      this.projectState.set('showTankBatteryTabs', this.showTankBatteryTabs);
      this.projectState.set('showSWDFacilityPlanningTab', this.showSWDFacilityPlanningTab);
      let exists = Object.keys(f).some(function (k) {
        return f[k] === false;
      });
      this.formValid = !exists;

      if (!this.requireAssetAllocation)
        f.assetmapping = true;

      this.validateTabs = f;
      this.formValid = true;
      this.validateTabs = this.allTabsValid;

    });
  }

  private tabInputProjectId(projectId: number) {
    this.projectExists = true;
    this.showNewProject = false;
  }

  // invoke the callCreateProject method of the ProjectCreateComponent child
  public async callNext(e) {
    this.isAddingProject = true;
    try {
      await this._projectCreateComponent().AddNewProject(e);
    }
    catch (e) {
      this.errorService.error('Error Adding New Project', e);
    }
    finally {
      this.isAddingProject = false;
    }
  }

  // read the emitted value of child to determine if a new project has just been created. If so, then display all of the tabs
  public readProjectExist(event: boolean) {
    this.projectExists = event;
  }

  public readHeaderTab(e: string): void {
    this.headerTabTitle = e;
  }
  public readHeaderTabTdv(e: boolean): void {
    this.headerTabTdv = e;
    this.determineAllStates();
  }

  public readParametersTab(e: string): void {
    this.parametersTabTitle = e;
  }

  public readParametersTabTdv(e: boolean): void {
    this.parametersTabTdv = e;
    this.determineAllStates();
  }

  public readAssetMappingTab(e: string): void {
    this.assetMappingTabTitle = e;
  }

  public readAssetMappingTabTdv(e: boolean): void {
    this.assetMappingTabTdv = e;
    this.determineAllStates();
  }

  public readAccountingTab(e: string): void {
    this.accountingTabTitle = e;
  }

  public readAccountingTabTdv(e: boolean): void {
    this.accountingTabTdv = e;
    this.determineAllStates();
  }

  public readEventLogTabTdv(e: boolean): void {
    this.eventLogTabTdv = e;
    this.determineAllStates();
  }

  public readPlanningTab(e: string): void {
    this.planningabTitle = e;
  }

  public readPlanningTabTdv(e: boolean): void {
    this.planningTabTdv = e;
    this.determineAllStates();
  }

  public readScopingTab(e: string): void {
    this.scopingTabTitle = e;
  }

  public readScopingTabTdv(e: boolean): void {
    this.scopingTabTdv = e;
    this.determineAllStates();
  }

  // read the emitted value of the child to determine if the parent should display the New Project tab
  public readShowNew(event: boolean) {
    this.showNewProject = event;
  }

  // read the emitted value of child to determine if the new project form is valid. If so, then display the Next button
  public readValidNewProject(e: boolean): void {
    this.isProjectValid = e;
  }

  public readTeamTab(e: string): void {
    this.teamTabTitle = e;
  }

  public readScheduleTab(e: string): void {
    this.scheduleTabTitle = e;
  }

  public readScheduleTabTdv(e: boolean): void {
    this.scheduleTabTdv = e;
    this.determineAllStates();
  }

  public readEstimateTab(e: string): void {
    this.estimatesTabTitle = e;
  }

  public readEstimateTabTdv(e: boolean): void {
    this.estimatesTabTdv = e;
    this.determineAllStates();
  }

  private determineAllStates(): void {
    let resolve: boolean = false;
    resolve = this.headerTabTdv || this.assetMappingTabTdv || this.accountingTabTdv
      || this.estimatesTabTdv || this.parametersTabTdv || this.eventLogTabTdv || this.planningTabTdv || this.scopingTabTdv;

    this.formState = resolve;
  }

  /**
   * A dirty way to manage tab notification according to the way it works now.
   * TODO: Create an independent module to handle validation and submission processing
   *
   * @param {*} forms
   * @returns
   * @memberof ProjectBaseScenarioComponent
   */
  public validateProjectTabs(forms: any) {
    if (forms) {
      let valid = true;
      for (let prop in forms) {
        if (!forms.hasOwnProperty(prop)) continue;

        if (!forms[prop]) {
          if (prop === 'header') {
            this.readHeaderTab('HEADER*');
            this.readTab('header');
          }
          if (prop === 'parameters') {
            this.readParametersTab('PARAMETERS*');
            this.readTab('parameters');
          }
          if (prop === 'team') {
            this.readTeamTab('TEAM*');
            this.readTab('team');
          }
          if (prop === 'accounting') {
            this.readAccountingTab('ACCOUNTING*');
            this.readTab('accounting');
          }
          if (prop === 'estimates') {
            this.readEstimateTab('ESTIMATES*');
            this.readTab('estimates');
          }
          if (prop === 'assetmapping') {
            this.readAssetMappingTab('ASSET*');
            this.readTab('assetmapping');
          }
          valid = false;
        } else {
          if (prop === 'header') {
            this.readHeaderTab('HEADER');
            this.unReadTab('header');
          }
          if (prop === 'parameters') {
            this.readParametersTab('PARAMETERS');
            this.unReadTab('parameters');
          }
          if (prop === 'team') {
            this.readTeamTab('TEAM');
            this.unReadTab('team');
          }
          if (prop === 'accounting') {
            this.readAccountingTab('ACCOUNTING');
            this.unReadTab('accounting');
          }
          if (prop === 'estimates') {
            this.readEstimateTab('ESTIMATES');
            this.unReadTab('estimates');
          }
          if (prop === 'assetmapping') {
            this.readAssetMappingTab('ASSET*');
            this.unReadTab('assetmapping');
          }
        }
      }
    }
  }

  public readTab(tab) {
    this.validateTab.push(tab);
  }

  public unReadTab(tab) {
    const index = this.validateTab.indexOf(tab);
    if (index > -1) {
      this.validateTab.splice(index, 1);
    }
  }

  public indicateValidTab(tab) {
    if (!this.validateTabs[tab]) {
      return true;
    }
    return this.validateTabs[tab];
  }

  public saveActivate(formState: boolean, formValid: boolean) {
    if (this.projectState.get('status') === ProjectStatusDescription.Draft || this.projectState.get('status') === ProjectStatusDescription.Reactivated) {
      return !!formState;
    } else {
      return !!(formState && formValid);
    }
  }

  private getParameterVisibility(projectTypeId: number, projectSubTypeId: number): boolean {
    // lp-12-20-1207: replace this code with call to ProjectParameterConfiguration table
    // this table doesn't exist yet, but will be soon.  Parameter tab visibility will be determined
    // by checking the ProjectTypeId in the ProjectParameterConfiguration table.  For now it's hardcoded.
    let visibility: boolean = false;
    if (projectTypeId === ProjectTypeId.PondBuild || (projectTypeId === ProjectTypeId.PondBuildOther) ||
      (projectTypeId === ProjectTypeId.DrillOilGasWell && (projectSubTypeId === 12)) ||
      (projectTypeId === ProjectTypeId.CompleteOilGasWell && projectSubTypeId === 24)
      || projectTypeId === ProjectTypeId.CompleteStimOAG || projectTypeId === ProjectTypeId.CompletePostStimOAG
      || projectTypeId === ProjectTypeId.Flowline || projectTypeId === ProjectTypeId.WellHookup ||
      projectTypeId === ProjectTypeId.WellPadFacility || (projectTypeId === ProjectTypeId.PadConstruction && this.environment.showPadConstructionParameters)) {
      visibility = true;
    }
    return visibility;

  }

  public setTabVisibilityFromProjectCreate(typeSubType: TypeSubType): void {
    let typeId: number = typeSubType.ProjectTypeId;
    let subTypeId: number = typeSubType.ProjectSubTypeId;
    let functionId: number = typeSubType.FunctionId;
    this.showParameterTab = this.getParameterVisibility(typeId, subTypeId);
    this.showTankBatteryTabs = typeId === ProjectTypeId.TankBattery && functionId === FunctionIds.Facilities;
    this.showSWDFacilityPlanningTab = typeId === ProjectTypeId.SaltWaterDisposal && functionId === FunctionIds.Facilities;
  }

  public saveTabs(e: any): void {
    if (this.headerTabTdv) { this._projectHeaderDataComponent().onSubmit(e); }
    if (this.assetMappingTabTdv) { this._projectAssetMappingComponent().saveChanges(); }

    if (this.parametersTabTdv) {
      this.saving = true;
      this._projectDetailsComponent().saveParameters(e);
    }

    if (this.planningTabTdv) { this._projectPlanningComponent().savePlanningTasks(e); }
    if (this.scopingTabTdv) { this._projectScopingIndexComponent().saveChanges(e); }
  }

  public detailsTabSaveComplete(e: boolean) {
    this.saving = !e;
  }

  public cancelModify(e: any) {
    if (this.headerTabTdv) { this._projectHeaderDataComponent().clearTDV(); }
    if (this.assetMappingTabTdv) { this._projectAssetMappingComponent().cancelChanges(); }
    if (this.parametersTabTdv) { this._projectDetailsComponent().cancelChanges(e); }
    if (this.planningTabTdv) { this._projectPlanningComponent().cancelChanges(e); }
    if (this.scopingTabTdv) { this._projectScopingIndexComponent().cancelChanges(e); }
  }
  public cancelReset(e) {
    this.closeNewProjectTab.emit(null);
    this.projectExists = false;
    this.showNewProject = false;
  }

  public async handleHeaderStatusChange(e: { Status: string; doRefreshTabs: boolean; completed: (any) => void }) {

    let newStatus = e.Status;
    this.projectState.set('status', newStatus);
    if (this.assetMappingTabTdv) { //adding this to avoid unwanted reactivation call
      await this._projectAssetMappingComponent().reactivateAssetTab();
    }
    e.completed(true);
  }

  // todo: remove handleTabSelect() method.  It runs on every tab click, and the original purpose for it no longer exists.
  public handleTabSelect(tab: SelectEvent) {
    let $idx = tab.index;
    if (!this.showParameterTab) {
      $idx = tab.index >= 1 ? tab.index + 1 : tab.index;
    }

    // Resources Tab is selected.  Run tab activity method to perform onSelected type activities
    if (tab.title === ProjectTabName.Resources) {
      const _projectResourcesComponent = this._projectResourcesComponent();
      if (_projectResourcesComponent) {
        _projectResourcesComponent.runTabActivity();
      }
    }

    // Estimates Tab is selected.  Run tab activity method to perform onSelected type activities
    const _projectEstimateHomeComponent = this._projectEstimateHomeComponent();
    if (tab.title === ProjectTabName.Estimates) {
      if (_projectEstimateHomeComponent) {
        _projectEstimateHomeComponent.runTabActivity();
      }
    }

    // Load Capital data when Scoping Tab is selected.
    if (this.projectProperties.ProjectId > 0 && !this.showParameterTab && tab.index === 4) {
      const _projectScopingIndexComponent = this._projectScopingIndexComponent();
      if (_projectScopingIndexComponent) {
        _projectScopingIndexComponent.runTabActivity();
      }
    }

    // Compare line items when Estimates tab is selected.
    if (this.projectProperties.ProjectId > 0 && tab.index === 5) {
      if (_projectEstimateHomeComponent) {
        _projectEstimateHomeComponent.runTabActivity();
      }
    }

    if (this.projectProperties.ProjectId > 0 && this.showParameterTab && tab.index === 1) {
      const _projectDetailsComponent = this._projectDetailsComponent();
      if (_projectDetailsComponent) {
        _projectDetailsComponent.runTabActivity();
      }
    }

    // Header Tab is selected. Run tab activity method.
    if (this.projectProperties.ProjectId > 0 && tab.index === 0) {
      const _projectHeaderDataComponent = this._projectHeaderDataComponent();
      if (!_projectHeaderDataComponent) return;
      _projectHeaderDataComponent.stdConfigSync();
    }

    this.tabContents[$idx] = true;
    this.selectedTabService.selectedTabData({ index: tab.index, title: tab.title, prevented: false });
  }
}